import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  MenuItem,
  Switch,
  FormControlLabel,
  InputAdornment,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  AddOutlined,
  EditOutlined,
  ReceiptLongOutlined,
  TuneOutlined,
  HubOutlined,
  PublicOutlined,
  MopedOutlined,
  SearchOutlined,
  RefreshOutlined,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import StatCard from '../components/StatCard';
import EmptyState from '../components/EmptyState';
import LoadingState from '../components/LoadingState';
import {
  restaurantsService,
  serviceZonesService,
  compensationService,
} from '../api/services';
import { useLanguage } from '../context/LanguageContext';
import { getApiErrorMessage } from '../utils/errorHandler';

const PricingPlans = () => {
  const { t, isRtl, dir } = useLanguage();
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [searchTerm, setSearchTerm] = useState('');

  // 1. Restaurant Pricing Plans State
  const [plans, setPlans] = useState([]);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [savingPlan, setSavingPlan] = useState(false);
  const [planForm, setPlanForm] = useState({
    planCode: '',
    planNameAr: '',
    effectiveFrom: new Date().toISOString().split('T')[0],
    effectiveTo: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    additionalOrderFeePercentage: 0,
    isActive: true,
  });

  // 2. Pricing Rules State
  const [rules, setRules] = useState([]);
  const [selectedRulePlanId, setSelectedRulePlanId] = useState('');
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [savingRule, setSavingRule] = useState(false);
  const [ruleForm, setRuleForm] = useState({
    restaurantPricingPlanId: '',
    restaurantBranchId: '',
    serviceZoneId: '',
    calculationType: 1, // 1: Fixed, 2: Distance
    fixedFee: 25.0,
    baseFee: 20.0,
    includedDistanceKm: 3.0,
    additionalKmFee: 3.5,
    minimumFee: 20.0,
    maximumFee: 150.0,
    isActive: true,
  });

  // 3. Branch Assignments State
  const [assignments, setAssignments] = useState([]);
  const [allBranches, setAllBranches] = useState([]);
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [assignmentForm, setAssignmentForm] = useState({
    restaurantBranchId: '',
    restaurantPricingPlanId: '',
    effectiveFrom: new Date().toISOString().split('T')[0],
    effectiveTo: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    isActive: true,
  });

  // 4. Service Zones State
  const [serviceZones, setServiceZones] = useState([]);
  const [zoneModalOpen, setZoneModalOpen] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [savingZone, setSavingZone] = useState(false);
  const [zoneForm, setZoneForm] = useState({
    zoneCode: '',
    zoneNameAr: '',
    zoneNameEn: '',
    centerLatitude: 30.0444,
    centerLongitude: 31.2357,
    priority: 1,
    isActive: true,
    boundaryWkt: '',
  });

  // 5. Courier Compensation Plans & Commission Rules State
  const [compPlans, setCompPlans] = useState([]);
  const [commissionRules, setCommissionRules] = useState([]);
  const [selectedCompPlanId, setSelectedCompPlanId] = useState('');
  const [compPlanModalOpen, setCompPlanModalOpen] = useState(false);
  const [editingCompPlan, setEditingCompPlan] = useState(null);
  const [savingCompPlan, setSavingCompPlan] = useState(false);
  const [compPlanForm, setCompPlanForm] = useState({
    planCode: '',
    planNameAr: '',
    compensationMode: 1,
    hourlyRate: 0,
    dailyRate: 0,
    additionalOrderPercentage: 0,
    effectiveFrom: new Date().toISOString().split('T')[0],
    effectiveTo: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    isActive: true,
  });

  const [commissionRuleModalOpen, setCommissionRuleModalOpen] = useState(false);
  const [editingCommissionRule, setEditingCommissionRule] = useState(null);
  const [savingCommissionRule, setSavingCommissionRule] = useState(false);
  const [commissionRuleForm, setCommissionRuleForm] = useState({
    courierCompensationPlanId: '',
    pickupRestaurantBranchId: '',
    destinationServiceZoneId: '',
    calculationType: 1,
    fixedAmount: 0,
    baseAmount: 0,
    includedDistanceKm: 0,
    additionalKmAmount: 0,
    minimumAmount: 0,
    maximumAmount: 0,
    isActive: true,
  });

  // Master Initial Fetch
  const loadAllData = async () => {
    setLoading(true);
    try {
      const [plansRes, zonesRes, compPlansRes] = await Promise.allSettled([
        restaurantsService.getPricingPlans(),
        serviceZonesService.getServiceZones({ pageSize: 100 }),
        compensationService.getPlans(),
      ]);

      const loadedPlans = plansRes.status === 'fulfilled' && plansRes.value
        ? (Array.isArray(plansRes.value) ? plansRes.value : (plansRes.value.items || []))
        : [];
      setPlans(loadedPlans);

      const loadedZones = zonesRes.status === 'fulfilled' && zonesRes.value
        ? (Array.isArray(zonesRes.value) ? zonesRes.value : (zonesRes.value.items || []))
        : [];
      setServiceZones(loadedZones);

      const loadedCompPlans = compPlansRes.status === 'fulfilled' && compPlansRes.value
        ? (Array.isArray(compPlansRes.value) ? compPlansRes.value : (compPlansRes.value.items || []))
        : [];
      setCompPlans(loadedCompPlans);

      if (loadedPlans.length > 0 && !selectedRulePlanId) {
        const firstPlanId = loadedPlans[0].restaurantPricingPlanId || loadedPlans[0].id;
        setSelectedRulePlanId(firstPlanId);
        fetchRulesForPlan(firstPlanId);
      }

      if (loadedCompPlans.length > 0 && !selectedCompPlanId) {
        const firstCompId = loadedCompPlans[0].courierCompensationPlanId || loadedCompPlans[0].id;
        setSelectedCompPlanId(firstCompId);
        fetchCommissionRules(firstCompId);
      }

      try {
        const branchesRes = await restaurantsService.getRestaurantBranches();
        const bList = Array.isArray(branchesRes) ? branchesRes : (branchesRes?.items || []);
        setAllBranches(bList);
      } catch (err) {
        console.warn('Branches fetch note:', err);
      }
    } catch (e) {
      console.warn('Pricing plans data load error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAllData();
  }, []);

  // Fetch Rules for a Specific Plan
  const fetchRulesForPlan = async (planId) => {
    if (!planId) return;
    try {
      const res = await restaurantsService.getPricingRules(planId);
      const list = Array.isArray(res) ? res : (res?.items || []);
      setRules(list);
    } catch (e) {
      console.warn('Rules fetch error:', e);
      setRules([]);
    }
  };

  // Fetch Commission Rules for a Courier Plan
  const fetchCommissionRules = async (planId) => {
    if (!planId) return;
    try {
      const res = await compensationService.getCommissionRules(planId);
      const list = Array.isArray(res) ? res : (res?.items || []);
      setCommissionRules(list);
    } catch (e) {
      console.warn('Commission rules fetch error:', e);
      setCommissionRules([]);
    }
  };

  // Fetch Assignments
  const fetchAssignments = async () => {
    try {
      const res = await restaurantsService.getPricingAssignments();
      const list = Array.isArray(res) ? res : (res?.items || []);
      setAssignments(list);
    } catch (e) {
      console.warn('Assignments fetch error:', e);
      setAssignments([]);
    }
  };

  useEffect(() => {
    if (currentTab === 1 && selectedRulePlanId) {
      fetchRulesForPlan(selectedRulePlanId);
    } else if (currentTab === 2) {
      fetchAssignments();
    } else if (currentTab === 4 && selectedCompPlanId) {
      fetchCommissionRules(selectedCompPlanId);
    }
  }, [currentTab, selectedRulePlanId, selectedCompPlanId]);

  // ========================================================
  // PLAN HANDLERS
  // ========================================================
  const handleOpenAddPlan = () => {
    setEditingPlan(null);
    setPlanForm({
      planCode: `PLAN-${Math.floor(100 + Math.random() * 900)}`,
      planNameAr: '',
      effectiveFrom: new Date().toISOString().split('T')[0],
      effectiveTo: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      additionalOrderFeePercentage: 0,
      isActive: true,
    });
    setPlanModalOpen(true);
  };

  const handleOpenEditPlan = (p) => {
    setEditingPlan(p);
    setPlanForm({
      planCode: p.planCode || '',
      planNameAr: p.planNameAr || p.name || '',
      effectiveFrom: p.effectiveFrom ? p.effectiveFrom.split('T')[0] : new Date().toISOString().split('T')[0],
      effectiveTo: p.effectiveTo ? p.effectiveTo.split('T')[0] : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      additionalOrderFeePercentage: p.additionalOrderFeePercentage || 0,
      isActive: p.isActive !== undefined ? p.isActive : true,
      rowVersion: p.rowVersion,
    });
    setPlanModalOpen(true);
  };

  const handleSavePlan = async (e) => {
    if (e) e.preventDefault();
    if (!planForm.planCode.trim() || !planForm.planNameAr.trim()) {
      setToast({
        open: true,
        message: isRtl ? 'يرجى إدخال كود واسم الخطة' : 'Please enter plan code and name',
        severity: 'error',
      });
      return;
    }

    setSavingPlan(true);
    try {
      if (editingPlan) {
        const id = editingPlan.restaurantPricingPlanId || editingPlan.id;
        await restaurantsService.updatePricingPlan(id, planForm);
        setToast({
          open: true,
          message: isRtl ? 'تم تحديث خطة التسعير بنجاح' : 'Pricing plan updated successfully',
          severity: 'success',
        });
      } else {
        await restaurantsService.createPricingPlan(planForm);
        setToast({
          open: true,
          message: isRtl ? 'تم إنشاء خطة التسعير بنجاح' : 'Pricing plan created successfully',
          severity: 'success',
        });
      }
      setPlanModalOpen(false);
      loadAllData();
    } catch (err) {
      setToast({ open: true, message: getApiErrorMessage(err), severity: 'error' });
    } finally {
      setSavingPlan(false);
    }
  };

  // ========================================================
  // RULE HANDLERS
  // ========================================================
  const handleOpenAddRule = (defaultPlanId) => {
    setEditingRule(null);
    setRuleForm({
      restaurantPricingPlanId: defaultPlanId || selectedRulePlanId || (plans[0]?.restaurantPricingPlanId || plans[0]?.id || ''),
      restaurantBranchId: '',
      serviceZoneId: serviceZones[0]?.serviceZoneId || serviceZones[0]?.id || '',
      calculationType: 1,
      fixedFee: 25.0,
      baseFee: 20.0,
      includedDistanceKm: 3.0,
      additionalKmFee: 3.5,
      minimumFee: 20.0,
      maximumFee: 150.0,
      isActive: true,
    });
    setRuleModalOpen(true);
  };

  const handleOpenEditRule = (r) => {
    setEditingRule(r);
    setRuleForm({
      restaurantPricingPlanId: r.restaurantPricingPlanId || selectedRulePlanId,
      restaurantBranchId: r.restaurantBranchId || '',
      serviceZoneId: r.serviceZoneId || '',
      calculationType: r.calculationType || 1,
      fixedFee: r.fixedFee || 0,
      baseFee: r.baseFee || 0,
      includedDistanceKm: r.includedDistanceKm || 0,
      additionalKmFee: r.additionalKmFee || 0,
      minimumFee: r.minimumFee || 0,
      maximumFee: r.maximumFee || 0,
      isActive: r.isActive !== undefined ? r.isActive : true,
      rowVersion: r.rowVersion,
    });
    setRuleModalOpen(true);
  };

  const handleSaveRule = async (e) => {
    if (e) e.preventDefault();
    if (!ruleForm.restaurantPricingPlanId) {
      setToast({ open: true, message: isRtl ? 'يرجى تحديد خطة التسعير' : 'Please select a pricing plan', severity: 'error' });
      return;
    }

    setSavingRule(true);
    try {
      if (editingRule) {
        const id = editingRule.restaurantPricingRuleId || editingRule.id;
        await restaurantsService.updatePricingRule(id, ruleForm);
        setToast({ open: true, message: isRtl ? 'تم تحديث قاعدة التسعير بنجاح' : 'Rule updated successfully', severity: 'success' });
      } else {
        await restaurantsService.createPricingRule(ruleForm);
        setToast({ open: true, message: isRtl ? 'تمت إضافة قاعدة التسعير بنجاح' : 'Rule added successfully', severity: 'success' });
      }
      setRuleModalOpen(false);
      fetchRulesForPlan(ruleForm.restaurantPricingPlanId);
    } catch (err) {
      setToast({ open: true, message: getApiErrorMessage(err), severity: 'error' });
    } finally {
      setSavingRule(false);
    }
  };

  // ========================================================
  // ASSIGNMENT HANDLERS
  // ========================================================
  const handleOpenAddAssignment = () => {
    setEditingAssignment(null);
    setAssignmentForm({
      restaurantBranchId: allBranches[0]?.restaurantBranchId || allBranches[0]?.id || '',
      restaurantPricingPlanId: plans[0]?.restaurantPricingPlanId || plans[0]?.id || '',
      effectiveFrom: new Date().toISOString().split('T')[0],
      effectiveTo: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      isActive: true,
    });
    setAssignmentModalOpen(true);
  };

  const handleSaveAssignment = async (e) => {
    if (e) e.preventDefault();
    if (!assignmentForm.restaurantBranchId || !assignmentForm.restaurantPricingPlanId) {
      setToast({ open: true, message: isRtl ? 'يرجى تحديد الفرع وخطة التسعير' : 'Please select branch and plan', severity: 'error' });
      return;
    }

    setSavingAssignment(true);
    try {
      if (editingAssignment) {
        const id = editingAssignment.restaurantBranchPricingAssignmentId || editingAssignment.id;
        await restaurantsService.updatePricingAssignment(id, assignmentForm);
        setToast({ open: true, message: isRtl ? 'تم تحديث ربط الخطة بنجاح' : 'Assignment updated successfully', severity: 'success' });
      } else {
        await restaurantsService.createPricingAssignment(assignmentForm);
        setToast({ open: true, message: isRtl ? 'تم ربط الخطة بالفرع بنجاح' : 'Plan assigned to branch successfully', severity: 'success' });
      }
      setAssignmentModalOpen(false);
      fetchAssignments();
    } catch (err) {
      setToast({ open: true, message: getApiErrorMessage(err), severity: 'error' });
    } finally {
      setSavingAssignment(false);
    }
  };

  // ========================================================
  // SERVICE ZONE HANDLERS
  // ========================================================
  const handleOpenAddZone = () => {
    setEditingZone(null);
    setZoneForm({
      zoneCode: `ZONE-${Math.floor(100 + Math.random() * 900)}`,
      zoneNameAr: '',
      zoneNameEn: '',
      centerLatitude: 30.0444,
      centerLongitude: 31.2357,
      priority: 1,
      isActive: true,
      boundaryWkt: '',
    });
    setZoneModalOpen(true);
  };

  const handleOpenEditZone = (z) => {
    setEditingZone(z);
    setZoneForm({
      zoneCode: z.zoneCode || '',
      zoneNameAr: z.zoneNameAr || '',
      zoneNameEn: z.zoneNameEn || '',
      centerLatitude: z.centerLatitude || 30.0444,
      centerLongitude: z.centerLongitude || 31.2357,
      priority: z.priority || 1,
      isActive: z.isActive !== undefined ? z.isActive : true,
      boundaryWkt: z.boundaryWkt || '',
      rowVersion: z.rowVersion,
    });
    setZoneModalOpen(true);
  };

  const handleSaveZone = async (e) => {
    if (e) e.preventDefault();
    if (!zoneForm.zoneCode.trim() || !zoneForm.zoneNameAr.trim()) {
      setToast({ open: true, message: isRtl ? 'يرجى إدخال كود واسم المنطقة' : 'Please enter zone code and name', severity: 'error' });
      return;
    }

    setSavingZone(true);
    try {
      if (editingZone) {
        const id = editingZone.serviceZoneId || editingZone.id;
        await serviceZonesService.updateServiceZone(id, zoneForm);
        setToast({ open: true, message: isRtl ? 'تم تحديث منطقة الخدمة بنجاح' : 'Service zone updated successfully', severity: 'success' });
      } else {
        await serviceZonesService.createServiceZone(zoneForm);
        setToast({ open: true, message: isRtl ? 'تمت إضافة منطقة الخدمة بنجاح' : 'Service zone created successfully', severity: 'success' });
      }
      setZoneModalOpen(false);
      loadAllData();
    } catch (err) {
      setToast({ open: true, message: getApiErrorMessage(err), severity: 'error' });
    } finally {
      setSavingZone(false);
    }
  };

  // ========================================================
  // COURIER COMPENSATION & COMMISSION HANDLERS
  // ========================================================
  const handleOpenAddCompPlan = () => {
    setEditingCompPlan(null);
    setCompPlanForm({
      planCode: `COMP-${Math.floor(100 + Math.random() * 900)}`,
      planNameAr: '',
      compensationMode: 1,
      hourlyRate: 0,
      dailyRate: 0,
      additionalOrderPercentage: 0,
      effectiveFrom: new Date().toISOString().split('T')[0],
      effectiveTo: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      isActive: true,
    });
    setCompPlanModalOpen(true);
  };

  const handleOpenEditCompPlan = (cp) => {
    setEditingCompPlan(cp);
    setCompPlanForm({
      planCode: cp.planCode || '',
      planNameAr: cp.planNameAr || '',
      compensationMode: cp.compensationMode || 1,
      hourlyRate: cp.hourlyRate || 0,
      dailyRate: cp.dailyRate || 0,
      additionalOrderPercentage: cp.additionalOrderPercentage || 0,
      effectiveFrom: cp.effectiveFrom ? cp.effectiveFrom.split('T')[0] : new Date().toISOString().split('T')[0],
      effectiveTo: cp.effectiveTo ? cp.effectiveTo.split('T')[0] : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      isActive: cp.isActive !== undefined ? cp.isActive : true,
      rowVersion: cp.rowVersion,
    });
    setCompPlanModalOpen(true);
  };

  const handleSaveCompPlan = async (e) => {
    if (e) e.preventDefault();
    if (!compPlanForm.planCode.trim() || !compPlanForm.planNameAr.trim()) {
      setToast({ open: true, message: isRtl ? 'يرجى إدخال كود واسم الخطة' : 'Please enter plan code and name', severity: 'error' });
      return;
    }

    setSavingCompPlan(true);
    try {
      if (editingCompPlan) {
        const id = editingCompPlan.courierCompensationPlanId || editingCompPlan.id;
        await compensationService.updatePlan(id, compPlanForm);
        setToast({ open: true, message: isRtl ? 'تم تحديث خطة تعويض الكابتن بنجاح' : 'Courier compensation plan updated successfully', severity: 'success' });
      } else {
        await compensationService.createPlan(compPlanForm);
        setToast({ open: true, message: isRtl ? 'تمت إضافة خطة تعويض الكابتن بنجاح' : 'Courier compensation plan created successfully', severity: 'success' });
      }
      setCompPlanModalOpen(false);
      loadAllData();
    } catch (err) {
      setToast({ open: true, message: getApiErrorMessage(err), severity: 'error' });
    } finally {
      setSavingCompPlan(false);
    }
  };

  const handleOpenAddCommissionRule = () => {
    setEditingCommissionRule(null);
    setCommissionRuleForm({
      courierCompensationPlanId: selectedCompPlanId || (compPlans[0]?.courierCompensationPlanId || compPlans[0]?.id || ''),
      pickupRestaurantBranchId: '',
      destinationServiceZoneId: '',
      calculationType: 1,
      fixedAmount: 0,
      baseAmount: 0,
      includedDistanceKm: 0,
      additionalKmAmount: 0,
      minimumAmount: 0,
      maximumAmount: 0,
      isActive: true,
    });
    setCommissionRuleModalOpen(true);
  };

  const handleOpenEditCommissionRule = (cr) => {
    setEditingCommissionRule(cr);
    setCommissionRuleForm({
      courierCompensationPlanId: cr.courierCompensationPlanId || selectedCompPlanId,
      pickupRestaurantBranchId: cr.pickupRestaurantBranchId || '',
      destinationServiceZoneId: cr.destinationServiceZoneId || '',
      calculationType: cr.calculationType || 1,
      fixedAmount: cr.fixedAmount || 0,
      baseAmount: cr.baseAmount || 0,
      includedDistanceKm: cr.includedDistanceKm || 0,
      additionalKmAmount: cr.additionalKmAmount || 0,
      minimumAmount: cr.minimumAmount || 0,
      maximumAmount: cr.maximumAmount || 0,
      isActive: cr.isActive !== undefined ? cr.isActive : true,
      rowVersion: cr.rowVersion,
    });
    setCommissionRuleModalOpen(true);
  };

  const handleSaveCommissionRule = async (e) => {
    if (e) e.preventDefault();
    if (!commissionRuleForm.courierCompensationPlanId) {
      setToast({ open: true, message: isRtl ? 'يرجى تحديد خطة التعويض' : 'Please select compensation plan', severity: 'error' });
      return;
    }

    setSavingCommissionRule(true);
    try {
      if (editingCommissionRule) {
        const id = editingCommissionRule.courierOrderCommissionRuleId || editingCommissionRule.id;
        await compensationService.updateCommissionRule(id, commissionRuleForm);
        setToast({ open: true, message: isRtl ? 'تم تحديث قاعدة العمولة بنجاح' : 'Commission rule updated successfully', severity: 'success' });
      } else {
        await compensationService.createCommissionRule(commissionRuleForm);
        setToast({ open: true, message: isRtl ? 'تمت إضافة قاعدة العمولة بنجاح' : 'Commission rule added successfully', severity: 'success' });
      }
      setCommissionRuleModalOpen(false);
      fetchCommissionRules(commissionRuleForm.courierCompensationPlanId);
    } catch (err) {
      setToast({ open: true, message: getApiErrorMessage(err), severity: 'error' });
    } finally {
      setSavingCommissionRule(false);
    }
  };

  // Filtered Lists
  const filteredPlans = useMemo(() => {
    return plans.filter((p) => {
      const q = searchTerm.toLowerCase();
      const code = (p.planCode || '').toLowerCase();
      const name = (p.planNameAr || p.name || '').toLowerCase();
      return code.includes(q) || name.includes(q);
    });
  }, [plans, searchTerm]);

  return (
    <Layout>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ textAlign: isRtl ? 'right' : 'left' }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.2 }}>
            {t('pricingPage.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('pricingPage.subtitle')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshOutlined sx={{ fontSize: 16 }} />}
            onClick={loadAllData}
            disabled={loading}
            sx={{ borderRadius: '8px', fontSize: '0.78rem', py: 0.6 }}
          >
            {t('refresh')}
          </Button>

          {currentTab === 0 && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddOutlined sx={{ fontSize: 16 }} />}
              onClick={handleOpenAddPlan}
              sx={{
                borderRadius: '8px',
                fontSize: '0.8rem',
                py: 0.6,
                background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
              }}
            >
              {t('pricingPage.addPlanBtn')}
            </Button>
          )}

          {currentTab === 1 && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddOutlined sx={{ fontSize: 16 }} />}
              onClick={() => handleOpenAddRule(selectedRulePlanId)}
              sx={{
                borderRadius: '8px',
                fontSize: '0.8rem',
                py: 0.6,
                background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
              }}
            >
              {t('pricingPage.addRuleBtn')}
            </Button>
          )}

          {currentTab === 2 && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddOutlined sx={{ fontSize: 16 }} />}
              onClick={handleOpenAddAssignment}
              sx={{
                borderRadius: '8px',
                fontSize: '0.8rem',
                py: 0.6,
                background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
              }}
            >
              {t('pricingPage.addAssignmentBtn')}
            </Button>
          )}

          {currentTab === 3 && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddOutlined sx={{ fontSize: 16 }} />}
              onClick={handleOpenAddZone}
              sx={{
                borderRadius: '8px',
                fontSize: '0.8rem',
                py: 0.6,
                background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
              }}
            >
              {t('pricingPage.addZoneBtn')}
            </Button>
          )}
        </Box>
      </Box>

      {/* KPI Stat Cards */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('pricingPage.stats.totalPlans')}
            value={plans.length.toString()}
            icon={ReceiptLongOutlined}
            color="#EF6C00"
            subtitle={`${plans.filter((p) => p.isActive !== false).length} ${t('active')}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('pricingPage.stats.totalRules')}
            value={rules.length.toString()}
            icon={TuneOutlined}
            color="#3B82F6"
            subtitle={isRtl ? 'قواعد تسعير مفصلة' : 'Detailed pricing rules'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('pricingPage.stats.serviceZones')}
            value={serviceZones.length.toString()}
            icon={PublicOutlined}
            color="#10B981"
            subtitle={isRtl ? 'نطاقات جغرافية معتمدة' : 'Approved coverage zones'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={isRtl ? 'خطط تعويض الكباتن' : 'Courier Comp Plans'}
            value={compPlans.length.toString()}
            icon={MopedOutlined}
            color="#8B5CF6"
            subtitle={`${compPlans.filter((c) => c.isActive !== false).length} ${t('active')}`}
          />
        </Grid>
      </Grid>

      {/* Navigation Tabs Card */}
      <Card sx={{ mb: 2.5, borderRadius: '12px' }}>
        <Tabs
          value={currentTab}
          onChange={(e, val) => setCurrentTab(val)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTab-root': {
              fontWeight: 700,
              fontSize: '0.84rem',
              py: 1.5,
              minHeight: 48,
              textTransform: 'none',
              gap: 1,
            },
          }}
        >
          <Tab icon={<ReceiptLongOutlined sx={{ fontSize: 18 }} />} iconPosition="start" label={t('pricingPage.tabs.plans')} />
          <Tab icon={<TuneOutlined sx={{ fontSize: 18 }} />} iconPosition="start" label={t('pricingPage.tabs.rules')} />
          <Tab icon={<HubOutlined sx={{ fontSize: 18 }} />} iconPosition="start" label={t('pricingPage.tabs.assignments')} />
          <Tab icon={<PublicOutlined sx={{ fontSize: 18 }} />} iconPosition="start" label={t('pricingPage.tabs.serviceZones')} />
          <Tab icon={<MopedOutlined sx={{ fontSize: 18 }} />} iconPosition="start" label={t('pricingPage.tabs.courierComp')} />
        </Tabs>
      </Card>

      {/* ======================================================== */}
      {/* TAB 0: RESTAURANT PRICING PLANS                          */}
      {/* ======================================================== */}
      {currentTab === 0 && (
        <Card sx={{ borderRadius: '12px' }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <TextField
                size="small"
                placeholder={t('search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ width: { xs: '100%', sm: 280 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined sx={{ color: 'text.secondary', fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {filteredPlans.length} {isRtl ? 'خطة تسعير متوفرة' : 'pricing plans available'}
              </Typography>
            </Box>

            {loading ? (
              <LoadingState />
            ) : filteredPlans.length === 0 ? (
              <EmptyState
                message={t('noData')}
                subtitle={isRtl ? 'يمكنك إضافة خطة تسعير جديدة من الزر أعلاه' : 'You can create a new pricing plan'}
              />
            ) : (
              <TableContainer>
                <Table size="small" sx={{ minWidth: 800 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 140 }}>
                        {t('pricingPage.tableHeaders.planCode')}
                      </TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 220 }}>
                        {t('pricingPage.tableHeaders.planName')}
                      </TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 200 }}>
                        {t('pricingPage.tableHeaders.validity')}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center !important', width: 130 }}>
                        {t('pricingPage.tableHeaders.additionalFee')}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center !important', width: 110 }}>
                        {t('status')}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center !important', width: 150 }}>
                        {t('actions')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPlans.map((p) => {
                      const id = p.restaurantPricingPlanId || p.id;
                      return (
                        <TableRow key={id} hover>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 800, color: 'primary.main', fontSize: '0.82rem' }}>
                              {p.planCode || `PLAN-${id}`}
                            </Typography>
                          </TableCell>

                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.84rem' }}>
                              {p.planNameAr || p.name || (isRtl ? 'خطة غير مسماة' : 'Unnamed Plan')}
                            </Typography>
                          </TableCell>

                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.74rem' }}>
                              {p.effectiveFrom ? p.effectiveFrom.split('T')[0] : '2026-01-01'} → {p.effectiveTo ? p.effectiveTo.split('T')[0] : '2026-12-31'}
                            </Typography>
                          </TableCell>

                          <TableCell sx={{ textAlign: 'center !important', whiteSpace: 'nowrap' }}>
                            <Chip
                              label={`${p.additionalOrderFeePercentage || 0}%`}
                              size="small"
                              variant="outlined"
                              color="warning"
                              sx={{ fontWeight: 700, fontSize: '0.72rem' }}
                            />
                          </TableCell>

                          <TableCell sx={{ textAlign: 'center !important' }}>
                            <Chip
                              label={p.isActive !== false ? t('active') : t('inactive')}
                              size="small"
                              color={p.isActive !== false ? 'success' : 'default'}
                              sx={{ fontWeight: 700, fontSize: '0.72rem' }}
                            />
                          </TableCell>

                          <TableCell sx={{ textAlign: 'center !important', whiteSpace: 'nowrap' }}>
                            <Tooltip title={isRtl ? 'عرض وإدارة قواعد التسعير' : 'View & Manage Rules'}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<TuneOutlined sx={{ fontSize: 14 }} />}
                                onClick={() => {
                                  setSelectedRulePlanId(id);
                                  setCurrentTab(1);
                                }}
                                sx={{ fontSize: '0.72rem', py: 0.3, px: 1, mr: 0.5, borderRadius: '6px' }}
                              >
                                {isRtl ? 'القواعد' : 'Rules'}
                              </Button>
                            </Tooltip>

                            <Tooltip title={t('edit')}>
                              <IconButton size="small" color="primary" onClick={() => handleOpenEditPlan(p)}>
                                <EditOutlined sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* ======================================================== */}
      {/* TAB 1: PRICING RULES MANAGEMENT                          */}
      {/* ======================================================== */}
      {currentTab === 1 && (
        <Card sx={{ borderRadius: '12px' }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 260 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                  {isRtl ? 'الخطة المستهدفة:' : 'Target Plan:'}
                </Typography>
                <TextField
                  select
                  size="small"
                  value={selectedRulePlanId || ''}
                  onChange={(e) => {
                    setSelectedRulePlanId(e.target.value);
                    fetchRulesForPlan(e.target.value);
                  }}
                  sx={{ minWidth: 200 }}
                >
                  {plans.map((p) => {
                    const id = p.restaurantPricingPlanId || p.id;
                    return (
                      <MenuItem key={id} value={id}>
                        {p.planCode} - {p.planNameAr}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Box>

              <Button
                variant="contained"
                size="small"
                startIcon={<AddOutlined sx={{ fontSize: 16 }} />}
                onClick={() => handleOpenAddRule(selectedRulePlanId)}
                sx={{
                  borderRadius: '8px',
                  fontSize: '0.78rem',
                  py: 0.5,
                  background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
                }}
              >
                {t('pricingPage.addRuleBtn')}
              </Button>
            </Box>

            {rules.length === 0 ? (
              <EmptyState
                message={isRtl ? 'لا توجد قواعد تسعير لهذه الخطة' : 'No pricing rules for this plan'}
                subtitle={isRtl ? 'أضف قاعدة تسعير تحدد الرسوم الثابتة أو الرسوم حسب المسافة والمناطق' : 'Add rules to define fixed or distance-based fees'}
              />
            ) : (
              <TableContainer>
                <Table size="small" sx={{ minWidth: 800 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 90 }}>
                        {t('pricingPage.tableHeaders.ruleId')}
                      </TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 160 }}>
                        {t('pricingPage.tableHeaders.calcType')}
                      </TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 280 }}>
                        {t('pricingPage.tableHeaders.feesSummary')}
                      </TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 140 }}>
                        {t('pricingPage.tableHeaders.zone')}
                      </TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 140 }}>
                        {t('pricingPage.tableHeaders.branch')}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center !important', width: 100 }}>
                        {t('status')}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center !important', width: 90 }}>
                        {t('actions')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rules.map((r) => {
                      const id = r.restaurantPricingRuleId || r.id;
                      const isFixed = r.calculationType === 1;
                      const matchedZone = serviceZones.find((z) => (z.serviceZoneId || z.id) === r.serviceZoneId);
                      const matchedBranch = allBranches.find((b) => (b.restaurantBranchId || b.id) === r.restaurantBranchId);

                      return (
                        <TableRow key={id} hover>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', fontWeight: 700 }}>
                            #{id}
                          </TableCell>

                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>
                            <Chip
                              label={isFixed ? t('pricingPage.calcTypes.fixed') : t('pricingPage.calcTypes.distance')}
                              size="small"
                              color={isFixed ? 'primary' : 'secondary'}
                              variant="outlined"
                              sx={{ fontWeight: 700, fontSize: '0.72rem' }}
                            />
                          </TableCell>

                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>
                            {isFixed ? (
                              <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.82rem' }}>
                                {r.fixedFee || 0} {t('currency')} <Typography component="span" variant="caption" color="text.secondary">({isRtl ? 'ثابت للطلب' : 'fixed'})</Typography>
                              </Typography>
                            ) : (
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                                  {r.baseFee || 0} {t('currency')} (أول {r.includedDistanceKm || 0} كم) + {r.additionalKmFee || 0} {t('currency')}/كم
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                  الحد الأدنى: {r.minimumFee || 0} {t('currency')} | الأقصى: {r.maximumFee || 0} {t('currency')}
                                </Typography>
                              </Box>
                            )}
                          </TableCell>

                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                              {matchedZone ? (matchedZone.zoneNameAr || matchedZone.zoneCode) : (isRtl ? 'جميع المناطق' : 'All Zones')}
                            </Typography>
                          </TableCell>

                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                              {matchedBranch ? (matchedBranch.branchNameAr || matchedBranch.branchCode) : (isRtl ? 'جميع الفروع' : 'All Branches')}
                            </Typography>
                          </TableCell>

                          <TableCell sx={{ textAlign: 'center !important' }}>
                            <Chip
                              label={r.isActive !== false ? t('active') : t('inactive')}
                              size="small"
                              color={r.isActive !== false ? 'success' : 'default'}
                              sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                            />
                          </TableCell>

                          <TableCell sx={{ textAlign: 'center !important' }}>
                            <Tooltip title={t('edit')}>
                              <IconButton size="small" color="primary" onClick={() => handleOpenEditRule(r)}>
                                <EditOutlined sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* ======================================================== */}
      {/* TAB 2: BRANCH PRICING ASSIGNMENTS                        */}
      {/* ======================================================== */}
      {currentTab === 2 && (
        <Card sx={{ borderRadius: '12px' }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {isRtl ? 'ربط فروع المطاعم بخطط الأسعار السارية' : 'Branch Plan Assignments'}
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddOutlined sx={{ fontSize: 16 }} />}
                onClick={handleOpenAddAssignment}
                sx={{
                  borderRadius: '8px',
                  fontSize: '0.78rem',
                  py: 0.5,
                  background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
                }}
              >
                {t('pricingPage.addAssignmentBtn')}
              </Button>
            </Box>

            {assignments.length === 0 ? (
              <EmptyState
                message={isRtl ? 'لا توجد تعيينات لخطط الفروع حالياً' : 'No branch plan assignments found'}
                subtitle={isRtl ? 'يمكنك ربط أي فرع مطعم بخطة التسعير المناسبة' : 'Assign pricing plans to branches'}
              />
            ) : (
              <TableContainer>
                <Table size="small" sx={{ minWidth: 700 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>{t('pricingPage.tableHeaders.branchName')}</TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>{t('pricingPage.tableHeaders.assignedPlan')}</TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>{t('pricingPage.tableHeaders.validity')}</TableCell>
                      <TableCell sx={{ textAlign: 'center !important' }}>{t('status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {assignments.map((a) => {
                      const id = a.restaurantBranchPricingAssignmentId || a.id;
                      const matchedBranch = allBranches.find((b) => (b.restaurantBranchId || b.id) === a.restaurantBranchId);
                      const matchedPlan = plans.find((p) => (p.restaurantPricingPlanId || p.id) === a.restaurantPricingPlanId);

                      return (
                        <TableRow key={id} hover>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', fontWeight: 700 }}>
                            {matchedBranch ? (matchedBranch.branchNameAr || matchedBranch.branchCode) : `Branch #${a.restaurantBranchId}`}
                          </TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>
                            <Chip
                              label={matchedPlan ? (matchedPlan.planNameAr || matchedPlan.planCode) : `Plan #${a.restaurantPricingPlanId}`}
                              color="primary"
                              variant="outlined"
                              size="small"
                              sx={{ fontWeight: 700 }}
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>
                            <Typography variant="caption" color="text.secondary">
                              {a.effectiveFrom ? a.effectiveFrom.split('T')[0] : '2026-01-01'} → {a.effectiveTo ? a.effectiveTo.split('T')[0] : '2026-12-31'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center !important' }}>
                            <Chip
                              label={a.isActive !== false ? t('active') : t('inactive')}
                              size="small"
                              color={a.isActive !== false ? 'success' : 'default'}
                              sx={{ fontWeight: 700 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* ======================================================== */}
      {/* TAB 3: GEOGRAPHIC SERVICE ZONES                          */}
      {/* ======================================================== */}
      {currentTab === 3 && (
        <Card sx={{ borderRadius: '12px' }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {isRtl ? 'مناطق التغطية والخدمة الجغرافية' : 'Geographic Service Zones'}
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddOutlined sx={{ fontSize: 16 }} />}
                onClick={handleOpenAddZone}
                sx={{
                  borderRadius: '8px',
                  fontSize: '0.78rem',
                  py: 0.5,
                  background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
                }}
              >
                {t('pricingPage.addZoneBtn')}
              </Button>
            </Box>

            {serviceZones.length === 0 ? (
              <EmptyState
                message={isRtl ? 'لا توجد مناطق خدمة مسجلة' : 'No service zones found'}
                subtitle={isRtl ? 'أضف مناطق الخدمة لتقسيم التسعير وتوجيه الطلبات جغرافياً' : 'Add delivery zones to manage geographic rates'}
              />
            ) : (
              <TableContainer>
                <Table size="small" sx={{ minWidth: 700 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 140 }}>
                        {t('pricingPage.tableHeaders.zoneCode')}
                      </TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 220 }}>
                        {t('pricingPage.tableHeaders.zoneName')}
                      </TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 180 }}>
                        {t('pricingPage.tableHeaders.coordinates')}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center !important', width: 100 }}>
                        {t('pricingPage.tableHeaders.priority')}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center !important', width: 100 }}>
                        {t('status')}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center !important', width: 90 }}>
                        {t('actions')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {serviceZones.map((z) => {
                      const id = z.serviceZoneId || z.id;
                      return (
                        <TableRow key={id} hover>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', fontWeight: 800, color: 'primary.main' }}>
                            {z.zoneCode || `ZONE-${id}`}
                          </TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', fontWeight: 700 }}>
                            {z.zoneNameAr || (isRtl ? 'منطقة غير مسماة' : 'Unnamed Zone')}
                            {z.zoneNameEn && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {z.zoneNameEn}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                              {z.centerLatitude?.toFixed(4)}, {z.centerLongitude?.toFixed(4)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center !important' }}>
                            <Chip label={`P${z.priority || 1}`} size="small" variant="outlined" sx={{ fontWeight: 800 }} />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center !important' }}>
                            <Chip
                              label={z.isActive !== false ? t('active') : t('inactive')}
                              size="small"
                              color={z.isActive !== false ? 'success' : 'default'}
                              sx={{ fontWeight: 700 }}
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center !important' }}>
                            <Tooltip title={t('edit')}>
                              <IconButton size="small" color="primary" onClick={() => handleOpenEditZone(z)}>
                                <EditOutlined sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* ======================================================== */}
      {/* TAB 4: COURIER COMPENSATION & COMMISSIONS                */}
      {/* ======================================================== */}
      {currentTab === 4 && (
        <Card sx={{ borderRadius: '12px' }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {isRtl ? 'خطط تعويض الكباتن وقواعد العمولات' : 'Courier Compensation & Commission Rules'}
              </Typography>
            </Box>

            {/* Compensation Plans Header & Button */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'primary.main' }}>
                {isRtl ? '1. خطط التعويض الأساسية للكباتن' : '1. Courier Compensation Plans'}
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddOutlined sx={{ fontSize: 16 }} />}
                onClick={handleOpenAddCompPlan}
                sx={{
                  borderRadius: '8px',
                  fontSize: '0.78rem',
                  py: 0.5,
                  background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
                }}
              >
                {isRtl ? 'إضافة خطة تعويض كابتن' : 'Add Courier Plan'}
              </Button>
            </Box>

            {compPlans.length === 0 ? (
              <EmptyState message={isRtl ? 'لا توجد خطط تعويض كباتن' : 'No courier compensation plans'} subtitle={isRtl ? 'اضغط على زر إضافة خطة تعويض كابتن للبدء' : 'Click Add Courier Plan to create one'} />
            ) : (
              <TableContainer sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>{t('pricingPage.tableHeaders.planCode')}</TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>{t('pricingPage.tableHeaders.planName')}</TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>{t('pricingPage.tableHeaders.hourlyDaily')}</TableCell>
                      <TableCell sx={{ textAlign: 'center !important' }}>{isRtl ? 'نسبة إضافية' : 'Bonus %'}</TableCell>
                      <TableCell sx={{ textAlign: 'center !important' }}>{t('status')}</TableCell>
                      <TableCell sx={{ textAlign: 'center !important' }}>{t('actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {compPlans.map((cp) => {
                      const id = cp.courierCompensationPlanId || cp.id;
                      return (
                        <TableRow key={id} hover>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', fontWeight: 800, color: 'primary.main' }}>
                            {cp.planCode || `COMP-${id}`}
                          </TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', fontWeight: 700 }}>
                            {cp.planNameAr || (isRtl ? 'خطة كابتن' : 'Courier Plan')}
                          </TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                              {cp.hourlyRate || 0} {t('currency')}/ساعة | {cp.dailyRate || 0} {t('currency')}/يوم
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center !important' }}>
                            <Chip label={`${cp.additionalOrderPercentage || 0}%`} size="small" variant="outlined" sx={{ fontWeight: 700 }} />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center !important' }}>
                            <Chip
                              label={cp.isActive !== false ? t('active') : t('inactive')}
                              size="small"
                              color={cp.isActive !== false ? 'success' : 'default'}
                              sx={{ fontWeight: 700 }}
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center !important' }}>
                            <Tooltip title={t('edit')}>
                              <IconButton size="small" color="primary" onClick={() => handleOpenEditCompPlan(cp)}>
                                <EditOutlined sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Commission Rules Section */}
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'primary.main' }}>
                  {isRtl ? '2. قواعد عمولات الطلبات حسب خطة الكابتن' : '2. Order Commission Rules'}
                </Typography>
                <TextField
                  select
                  size="small"
                  value={selectedCompPlanId || ''}
                  onChange={(e) => {
                    setSelectedCompPlanId(e.target.value);
                    fetchCommissionRules(e.target.value);
                  }}
                  sx={{ minWidth: 200 }}
                >
                  {compPlans.map((cp) => {
                    const id = cp.courierCompensationPlanId || cp.id;
                    return (
                      <MenuItem key={id} value={id}>
                        {cp.planCode} - {cp.planNameAr}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Box>

              <Button
                variant="outlined"
                size="small"
                startIcon={<AddOutlined sx={{ fontSize: 16 }} />}
                onClick={handleOpenAddCommissionRule}
                disabled={!selectedCompPlanId && compPlans.length === 0}
                sx={{
                  borderRadius: '8px',
                  fontSize: '0.78rem',
                  py: 0.5,
                }}
              >
                {isRtl ? 'إضافة قاعدة عمولة' : 'Add Commission Rule'}
              </Button>
            </Box>

            {commissionRules.length === 0 ? (
              <EmptyState message={isRtl ? 'لا توجد قواعد عمولات مسجلة لهذه الخطة' : 'No commission rules for this plan'} subtitle={isRtl ? 'اضغط على إضافة قاعدة عمولة لربط أسعار العمولات بالخطة' : 'Click Add Commission Rule to define commissions'} />
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>{t('pricingPage.tableHeaders.ruleId')}</TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>{t('pricingPage.tableHeaders.calcType')}</TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>{isRtl ? 'قيمة العمولة' : 'Commission Amount'}</TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>{t('pricingPage.tableHeaders.pickupBranch')}</TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>{t('pricingPage.tableHeaders.destZone')}</TableCell>
                      <TableCell sx={{ textAlign: 'center !important' }}>{t('actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {commissionRules.map((cr) => {
                      const id = cr.courierOrderCommissionRuleId || cr.id;
                      const isFixed = cr.calculationType === 1;
                      return (
                        <TableRow key={id} hover>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', fontWeight: 700 }}>#{id}</TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>
                            <Chip label={isFixed ? t('pricingPage.calcTypes.fixed') : t('pricingPage.calcTypes.distance')} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', fontWeight: 700 }}>
                            {isFixed ? `${cr.fixedAmount || 0} ${t('currency')}` : `${cr.baseAmount || 0} + ${cr.additionalKmAmount || 0}/كم`}
                          </TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>
                            {cr.pickupRestaurantBranchId ? `Branch #${cr.pickupRestaurantBranchId}` : (isRtl ? 'جميع الفروع' : 'All Branches')}
                          </TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>
                            {cr.destinationServiceZoneId ? `Zone #${cr.destinationServiceZoneId}` : (isRtl ? 'جميع المناطق' : 'All Zones')}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center !important' }}>
                            <Tooltip title={t('edit')}>
                              <IconButton size="small" color="primary" onClick={() => handleOpenEditCommissionRule(cr)}>
                                <EditOutlined sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* ======================================================== */}
      {/* DIALOG 1: ADD/EDIT PRICING PLAN                          */}
      {/* ======================================================== */}
      <Dialog
        open={planModalOpen}
        onClose={() => setPlanModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '14px', direction: dir, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', pb: 1, textAlign: isRtl ? 'right' : 'left' }}>
          {editingPlan ? t('pricingPage.editPlanBtn') : t('pricingPage.addPlanBtn')}
        </DialogTitle>
        <DialogContent>
          <Box component="form" noValidate onSubmit={handleSavePlan} sx={{ display: 'flex', flexDirection: 'column', gap: 1.8, mt: 1 }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('pricingPage.fields.planCode')}
                  fullWidth
                  size="small"
                  value={planForm.planCode}
                  onChange={(e) => setPlanForm({ ...planForm, planCode: e.target.value })}
                  required
                  placeholder="e.g. PLAN-CAIRO-01"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('pricingPage.fields.planNameAr')}
                  fullWidth
                  size="small"
                  value={planForm.planNameAr}
                  onChange={(e) => setPlanForm({ ...planForm, planNameAr: e.target.value })}
                  required
                  placeholder="e.g. خطة القاهرة الكبرى"
                />
              </Grid>
            </Grid>

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('pricingPage.fields.effectiveFrom')}
                  type="date"
                  fullWidth
                  size="small"
                  value={planForm.effectiveFrom}
                  onChange={(e) => setPlanForm({ ...planForm, effectiveFrom: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('pricingPage.fields.effectiveTo')}
                  type="date"
                  fullWidth
                  size="small"
                  value={planForm.effectiveTo}
                  onChange={(e) => setPlanForm({ ...planForm, effectiveTo: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('pricingPage.fields.additionalOrderFeePercentage')}
                  type="number"
                  fullWidth
                  size="small"
                  value={planForm.additionalOrderFeePercentage}
                  onChange={(e) => setPlanForm({ ...planForm, additionalOrderFeePercentage: parseFloat(e.target.value) || 0 })}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={planForm.isActive}
                      onChange={(e) => setPlanForm({ ...planForm, isActive: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={t('pricingPage.fields.isActive')}
                  sx={{ ml: 0 }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5, display: 'flex', justifyContent: isRtl ? 'flex-start' : 'flex-end', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleSavePlan}
            disabled={savingPlan}
            sx={{
              background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
              fontWeight: 700,
              minWidth: 100,
            }}
          >
            {savingPlan ? <CircularProgress size={18} color="inherit" /> : t('save')}
          </Button>
          <Button onClick={() => setPlanModalOpen(false)} color="inherit" size="small">
            {t('cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ======================================================== */}
      {/* DIALOG 2: ADD/EDIT PRICING RULE                          */}
      {/* ======================================================== */}
      <Dialog
        open={ruleModalOpen}
        onClose={() => setRuleModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '14px', direction: dir, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', pb: 1, textAlign: isRtl ? 'right' : 'left' }}>
          {editingRule ? t('pricingPage.editRuleBtn') : t('pricingPage.addRuleBtn')}
        </DialogTitle>
        <DialogContent>
          <Box component="form" noValidate onSubmit={handleSaveRule} sx={{ display: 'flex', flexDirection: 'column', gap: 1.8, mt: 1 }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label={t('pricingPage.tabs.plans')}
                  fullWidth
                  size="small"
                  value={ruleForm.restaurantPricingPlanId || ''}
                  onChange={(e) => setRuleForm({ ...ruleForm, restaurantPricingPlanId: e.target.value })}
                  required
                >
                  {plans.map((p) => {
                    const id = p.restaurantPricingPlanId || p.id;
                    return (
                      <MenuItem key={id} value={id}>
                        {p.planCode} - {p.planNameAr}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label={t('pricingPage.fields.calculationType')}
                  fullWidth
                  size="small"
                  value={ruleForm.calculationType}
                  onChange={(e) => setRuleForm({ ...ruleForm, calculationType: Number(e.target.value) })}
                >
                  <MenuItem value={1}>{t('pricingPage.calcTypes.fixed')}</MenuItem>
                  <MenuItem value={2}>{t('pricingPage.calcTypes.distance')}</MenuItem>
                </TextField>
              </Grid>
            </Grid>

            {/* Fixed Fee Form */}
            {ruleForm.calculationType === 1 ? (
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <TextField
                    label={t('pricingPage.fields.fixedFee')}
                    type="number"
                    fullWidth
                    size="small"
                    value={ruleForm.fixedFee}
                    onChange={(e) => setRuleForm({ ...ruleForm, fixedFee: parseFloat(e.target.value) || 0 })}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">{t('currency')}</InputAdornment>,
                    }}
                  />
                </Grid>
              </Grid>
            ) : (
              /* Distance-based Fee Form */
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t('pricingPage.fields.baseFee')}
                    type="number"
                    fullWidth
                    size="small"
                    value={ruleForm.baseFee}
                    onChange={(e) => setRuleForm({ ...ruleForm, baseFee: parseFloat(e.target.value) || 0 })}
                    InputProps={{ endAdornment: <InputAdornment position="end">{t('currency')}</InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t('pricingPage.fields.includedDistanceKm')}
                    type="number"
                    fullWidth
                    size="small"
                    value={ruleForm.includedDistanceKm}
                    onChange={(e) => setRuleForm({ ...ruleForm, includedDistanceKm: parseFloat(e.target.value) || 0 })}
                    InputProps={{ endAdornment: <InputAdornment position="end">KM</InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label={t('pricingPage.fields.additionalKmFee')}
                    type="number"
                    fullWidth
                    size="small"
                    value={ruleForm.additionalKmFee}
                    onChange={(e) => setRuleForm({ ...ruleForm, additionalKmFee: parseFloat(e.target.value) || 0 })}
                    InputProps={{ endAdornment: <InputAdornment position="end">{t('currency')}/KM</InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label={t('pricingPage.fields.minimumFee')}
                    type="number"
                    fullWidth
                    size="small"
                    value={ruleForm.minimumFee}
                    onChange={(e) => setRuleForm({ ...ruleForm, minimumFee: parseFloat(e.target.value) || 0 })}
                    InputProps={{ endAdornment: <InputAdornment position="end">{t('currency')}</InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label={t('pricingPage.fields.maximumFee')}
                    type="number"
                    fullWidth
                    size="small"
                    value={ruleForm.maximumFee}
                    onChange={(e) => setRuleForm({ ...ruleForm, maximumFee: parseFloat(e.target.value) || 0 })}
                    InputProps={{ endAdornment: <InputAdornment position="end">{t('currency')}</InputAdornment> }}
                  />
                </Grid>
              </Grid>
            )}

            {/* Scope Overrides: Service Zone & Branch */}
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label={t('pricingPage.fields.serviceZone')}
                  fullWidth
                  size="small"
                  value={ruleForm.serviceZoneId || ''}
                  onChange={(e) => setRuleForm({ ...ruleForm, serviceZoneId: e.target.value })}
                  helperText={isRtl ? 'اتركه فارغاً للتطبيق على جميع المناطق' : 'Leave empty for all zones'}
                >
                  <MenuItem value="">{isRtl ? 'جميع المناطق الجغرافية' : 'All Zones'}</MenuItem>
                  {serviceZones.map((z) => {
                    const id = z.serviceZoneId || z.id;
                    return (
                      <MenuItem key={id} value={id}>
                        {z.zoneCode} - {z.zoneNameAr}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label={t('pricingPage.fields.restaurantBranch')}
                  fullWidth
                  size="small"
                  value={ruleForm.restaurantBranchId || ''}
                  onChange={(e) => setRuleForm({ ...ruleForm, restaurantBranchId: e.target.value })}
                  helperText={isRtl ? 'اتركه فارغاً للتطبيق على جميع الفروع' : 'Leave empty for all branches'}
                >
                  <MenuItem value="">{isRtl ? 'جميع الفروع' : 'All Branches'}</MenuItem>
                  {allBranches.map((b) => {
                    const id = b.restaurantBranchId || b.id;
                    return (
                      <MenuItem key={id} value={id}>
                        {b.branchCode} - {b.branchNameAr}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>
            </Grid>

            <FormControlLabel
              control={
                <Switch
                  checked={ruleForm.isActive}
                  onChange={(e) => setRuleForm({ ...ruleForm, isActive: e.target.checked })}
                  color="primary"
                />
              }
              label={t('pricingPage.fields.isActive')}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5, display: 'flex', justifyContent: isRtl ? 'flex-start' : 'flex-end', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleSaveRule}
            disabled={savingRule}
            sx={{
              background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
              fontWeight: 700,
              minWidth: 100,
            }}
          >
            {savingRule ? <CircularProgress size={18} color="inherit" /> : t('save')}
          </Button>
          <Button onClick={() => setRuleModalOpen(false)} color="inherit" size="small">
            {t('cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ======================================================== */}
      {/* DIALOG 3: ADD/EDIT BRANCH ASSIGNMENT                     */}
      {/* ======================================================== */}
      <Dialog
        open={assignmentModalOpen}
        onClose={() => setAssignmentModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '14px', direction: dir, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', pb: 1, textAlign: isRtl ? 'right' : 'left' }}>
          {t('pricingPage.addAssignmentBtn')}
        </DialogTitle>
        <DialogContent>
          <Box component="form" noValidate onSubmit={handleSaveAssignment} sx={{ display: 'flex', flexDirection: 'column', gap: 1.8, mt: 1 }}>
            <TextField
              select
              label={t('pricingPage.tableHeaders.branchName')}
              fullWidth
              size="small"
              value={assignmentForm.restaurantBranchId}
              onChange={(e) => setAssignmentForm({ ...assignmentForm, restaurantBranchId: e.target.value })}
              required
            >
              {allBranches.map((b) => {
                const id = b.restaurantBranchId || b.id;
                return (
                  <MenuItem key={id} value={id}>
                    {b.branchCode} - {b.branchNameAr}
                  </MenuItem>
                );
              })}
            </TextField>

            <TextField
              select
              label={t('pricingPage.tableHeaders.assignedPlan')}
              fullWidth
              size="small"
              value={assignmentForm.restaurantPricingPlanId}
              onChange={(e) => setAssignmentForm({ ...assignmentForm, restaurantPricingPlanId: e.target.value })}
              required
            >
              {plans.map((p) => {
                const id = p.restaurantPricingPlanId || p.id;
                return (
                  <MenuItem key={id} value={id}>
                    {p.planCode} - {p.planNameAr}
                  </MenuItem>
                );
              })}
            </TextField>

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('pricingPage.fields.effectiveFrom')}
                  type="date"
                  fullWidth
                  size="small"
                  value={assignmentForm.effectiveFrom}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, effectiveFrom: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('pricingPage.fields.effectiveTo')}
                  type="date"
                  fullWidth
                  size="small"
                  value={assignmentForm.effectiveTo}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, effectiveTo: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5, display: 'flex', justifyContent: isRtl ? 'flex-start' : 'flex-end', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleSaveAssignment}
            disabled={savingAssignment}
            sx={{
              background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
              fontWeight: 700,
            }}
          >
            {savingAssignment ? <CircularProgress size={18} color="inherit" /> : t('save')}
          </Button>
          <Button onClick={() => setAssignmentModalOpen(false)} color="inherit" size="small">
            {t('cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ======================================================== */}
      {/* DIALOG 4: ADD/EDIT SERVICE ZONE                          */}
      {/* ======================================================== */}
      <Dialog
        open={zoneModalOpen}
        onClose={() => setZoneModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '14px', direction: dir, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', pb: 1, textAlign: isRtl ? 'right' : 'left' }}>
          {editingZone ? t('pricingPage.editRuleBtn') : t('pricingPage.addZoneBtn')}
        </DialogTitle>
        <DialogContent>
          <Box component="form" noValidate onSubmit={handleSaveZone} sx={{ display: 'flex', flexDirection: 'column', gap: 1.8, mt: 1 }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('pricingPage.fields.zoneCode')}
                  fullWidth
                  size="small"
                  value={zoneForm.zoneCode}
                  onChange={(e) => setZoneForm({ ...zoneForm, zoneCode: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('pricingPage.fields.priority')}
                  type="number"
                  fullWidth
                  size="small"
                  value={zoneForm.priority}
                  onChange={(e) => setZoneForm({ ...zoneForm, priority: Number(e.target.value) || 1 })}
                />
              </Grid>
            </Grid>

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('pricingPage.fields.zoneNameAr')}
                  fullWidth
                  size="small"
                  value={zoneForm.zoneNameAr}
                  onChange={(e) => setZoneForm({ ...zoneForm, zoneNameAr: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('pricingPage.fields.zoneNameEn')}
                  fullWidth
                  size="small"
                  value={zoneForm.zoneNameEn}
                  onChange={(e) => setZoneForm({ ...zoneForm, zoneNameEn: e.target.value })}
                />
              </Grid>
            </Grid>

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('pricingPage.fields.latitude')}
                  type="number"
                  fullWidth
                  size="small"
                  value={zoneForm.centerLatitude}
                  onChange={(e) => setZoneForm({ ...zoneForm, centerLatitude: parseFloat(e.target.value) || 0 })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('pricingPage.fields.longitude')}
                  type="number"
                  fullWidth
                  size="small"
                  value={zoneForm.centerLongitude}
                  onChange={(e) => setZoneForm({ ...zoneForm, centerLongitude: parseFloat(e.target.value) || 0 })}
                />
              </Grid>
            </Grid>

            <TextField
              label={isRtl ? 'حدود النطاق الجغرافي (WKT Polygon)' : 'Boundary WKT Polygon'}
              placeholder="POLYGON ((31.2 30.0, 31.3 30.0, 31.3 30.1, 31.2 30.1, 31.2 30.0))"
              multiline
              rows={2}
              fullWidth
              size="small"
              value={zoneForm.boundaryWkt || ''}
              onChange={(e) => setZoneForm({ ...zoneForm, boundaryWkt: e.target.value })}
              helperText={isRtl ? 'سيتم إنشاء مضلع تغطية تلقائي (5 كم) حول الإحداثيات إذا تُرك فارغاً' : 'Auto-generates a 5km polygon around center if left empty'}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={zoneForm.isActive}
                  onChange={(e) => setZoneForm({ ...zoneForm, isActive: e.target.checked })}
                  color="primary"
                />
              }
              label={t('pricingPage.fields.isActive')}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5, display: 'flex', justifyContent: isRtl ? 'flex-start' : 'flex-end', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleSaveZone}
            disabled={savingZone}
            sx={{
              background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
              fontWeight: 700,
            }}
          >
            {savingZone ? <CircularProgress size={18} color="inherit" /> : t('save')}
          </Button>
          <Button onClick={() => setZoneModalOpen(false)} color="inherit" size="small">
            {t('cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ======================================================== */}
      {/* DIALOG 5: ADD/EDIT COURIER COMPENSATION PLAN             */}
      {/* ======================================================== */}
      <Dialog
        open={compPlanModalOpen}
        onClose={() => setCompPlanModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '14px', direction: dir, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', pb: 1, textAlign: isRtl ? 'right' : 'left' }}>
          {editingCompPlan ? (isRtl ? 'تعديل خطة تعويض الكابتن' : 'Edit Courier Plan') : (isRtl ? 'إضافة خطة تعويض كابتن' : 'Add Courier Compensation Plan')}
        </DialogTitle>
        <DialogContent>
          <Box component="form" noValidate onSubmit={handleSaveCompPlan} sx={{ display: 'flex', flexDirection: 'column', gap: 1.8, mt: 1 }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('pricingPage.fields.planCode')}
                  fullWidth
                  size="small"
                  value={compPlanForm.planCode}
                  onChange={(e) => setCompPlanForm({ ...compPlanForm, planCode: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('pricingPage.fields.planNameAr')}
                  fullWidth
                  size="small"
                  value={compPlanForm.planNameAr}
                  onChange={(e) => setCompPlanForm({ ...compPlanForm, planNameAr: e.target.value })}
                  required
                />
              </Grid>
            </Grid>

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={isRtl ? 'أجر الساعة' : 'Hourly Rate'}
                  type="number"
                  fullWidth
                  size="small"
                  value={compPlanForm.hourlyRate}
                  onChange={(e) => setCompPlanForm({ ...compPlanForm, hourlyRate: parseFloat(e.target.value) || 0 })}
                  InputProps={{ endAdornment: <InputAdornment position="end">{t('currency')}</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={isRtl ? 'أجر اليوم' : 'Daily Rate'}
                  type="number"
                  fullWidth
                  size="small"
                  value={compPlanForm.dailyRate}
                  onChange={(e) => setCompPlanForm({ ...compPlanForm, dailyRate: parseFloat(e.target.value) || 0 })}
                  InputProps={{ endAdornment: <InputAdornment position="end">{t('currency')}</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label={isRtl ? 'نسبة كل طلب إضافي' : 'Bonus % per Order'}
                  type="number"
                  fullWidth
                  size="small"
                  value={compPlanForm.additionalOrderPercentage}
                  onChange={(e) => setCompPlanForm({ ...compPlanForm, additionalOrderPercentage: parseFloat(e.target.value) || 0 })}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('pricingPage.fields.effectiveFrom')}
                  type="date"
                  fullWidth
                  size="small"
                  value={compPlanForm.effectiveFrom}
                  onChange={(e) => setCompPlanForm({ ...compPlanForm, effectiveFrom: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('pricingPage.fields.effectiveTo')}
                  type="date"
                  fullWidth
                  size="small"
                  value={compPlanForm.effectiveTo}
                  onChange={(e) => setCompPlanForm({ ...compPlanForm, effectiveTo: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <FormControlLabel
              control={
                <Switch
                  checked={compPlanForm.isActive}
                  onChange={(e) => setCompPlanForm({ ...compPlanForm, isActive: e.target.checked })}
                  color="primary"
                />
              }
              label={t('pricingPage.fields.isActive')}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5, display: 'flex', justifyContent: isRtl ? 'flex-start' : 'flex-end', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleSaveCompPlan}
            disabled={savingCompPlan}
            sx={{
              background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
              fontWeight: 700,
            }}
          >
            {savingCompPlan ? <CircularProgress size={18} color="inherit" /> : t('save')}
          </Button>
          <Button onClick={() => setCompPlanModalOpen(false)} color="inherit" size="small">
            {t('cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ======================================================== */}
      {/* DIALOG 6: ADD/EDIT COMMISSION RULE                       */}
      {/* ======================================================== */}
      <Dialog
        open={commissionRuleModalOpen}
        onClose={() => setCommissionRuleModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '14px', direction: dir, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem', pb: 1, textAlign: isRtl ? 'right' : 'left' }}>
          {editingCommissionRule ? (isRtl ? 'تعديل قاعدة العمولة' : 'Edit Commission Rule') : (isRtl ? 'إضافة قاعدة عمولة جديدة' : 'Add Commission Rule')}
        </DialogTitle>
        <DialogContent>
          <Box component="form" noValidate onSubmit={handleSaveCommissionRule} sx={{ display: 'flex', flexDirection: 'column', gap: 1.8, mt: 1 }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label={isRtl ? 'خطة تعويض الكابتن' : 'Compensation Plan'}
                  fullWidth
                  size="small"
                  value={commissionRuleForm.courierCompensationPlanId}
                  onChange={(e) => setCommissionRuleForm({ ...commissionRuleForm, courierCompensationPlanId: e.target.value })}
                  required
                >
                  {compPlans.map((cp) => {
                    const id = cp.courierCompensationPlanId || cp.id;
                    return (
                      <MenuItem key={id} value={id}>
                        {cp.planCode} - {cp.planNameAr}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label={t('pricingPage.fields.calculationType')}
                  fullWidth
                  size="small"
                  value={commissionRuleForm.calculationType}
                  onChange={(e) => setCommissionRuleForm({ ...commissionRuleForm, calculationType: Number(e.target.value) })}
                >
                  <MenuItem value={1}>{t('pricingPage.calcTypes.fixed')}</MenuItem>
                  <MenuItem value={2}>{t('pricingPage.calcTypes.distance')}</MenuItem>
                </TextField>
              </Grid>
            </Grid>

            {/* Fixed vs Distance */}
            {commissionRuleForm.calculationType === 1 ? (
              <TextField
                label={isRtl ? 'مبلغ العمولة الثابت لكل طلب' : 'Fixed Commission Amount'}
                type="number"
                fullWidth
                size="small"
                value={commissionRuleForm.fixedAmount}
                onChange={(e) => setCommissionRuleForm({ ...commissionRuleForm, fixedAmount: parseFloat(e.target.value) || 0 })}
                InputProps={{ endAdornment: <InputAdornment position="end">{t('currency')}</InputAdornment> }}
              />
            ) : (
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={isRtl ? 'العمولة الأساسية' : 'Base Commission'}
                    type="number"
                    fullWidth
                    size="small"
                    value={commissionRuleForm.baseAmount}
                    onChange={(e) => setCommissionRuleForm({ ...commissionRuleForm, baseAmount: parseFloat(e.target.value) || 0 })}
                    InputProps={{ endAdornment: <InputAdornment position="end">{t('currency')}</InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={isRtl ? 'المسافة المشمولة (كم)' : 'Included Distance (KM)'}
                    type="number"
                    fullWidth
                    size="small"
                    value={commissionRuleForm.includedDistanceKm}
                    onChange={(e) => setCommissionRuleForm({ ...commissionRuleForm, includedDistanceKm: parseFloat(e.target.value) || 0 })}
                    InputProps={{ endAdornment: <InputAdornment position="end">KM</InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label={isRtl ? 'عمولة كل كم إضافي' : 'Additional KM Rate'}
                    type="number"
                    fullWidth
                    size="small"
                    value={commissionRuleForm.additionalKmAmount}
                    onChange={(e) => setCommissionRuleForm({ ...commissionRuleForm, additionalKmAmount: parseFloat(e.target.value) || 0 })}
                    InputProps={{ endAdornment: <InputAdornment position="end">{t('currency')}/KM</InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label={isRtl ? 'الحد الأدنى للعمولة' : 'Minimum Commission'}
                    type="number"
                    fullWidth
                    size="small"
                    value={commissionRuleForm.minimumAmount}
                    onChange={(e) => setCommissionRuleForm({ ...commissionRuleForm, minimumAmount: parseFloat(e.target.value) || 0 })}
                    InputProps={{ endAdornment: <InputAdornment position="end">{t('currency')}</InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label={isRtl ? 'الحد الأقصى للعمولة' : 'Maximum Commission'}
                    type="number"
                    fullWidth
                    size="small"
                    value={commissionRuleForm.maximumAmount}
                    onChange={(e) => setCommissionRuleForm({ ...commissionRuleForm, maximumAmount: parseFloat(e.target.value) || 0 })}
                    InputProps={{ endAdornment: <InputAdornment position="end">{t('currency')}</InputAdornment> }}
                  />
                </Grid>
              </Grid>
            )}

            {/* Scope: Branch & Zone */}
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label={isRtl ? 'فرع المطعم المستلم منه' : 'Pickup Restaurant Branch'}
                  fullWidth
                  size="small"
                  value={commissionRuleForm.pickupRestaurantBranchId || ''}
                  onChange={(e) => setCommissionRuleForm({ ...commissionRuleForm, pickupRestaurantBranchId: e.target.value })}
                  helperText={isRtl ? 'اتركه فارغاً للتطبيق على كافة الفروع' : 'Leave empty for all branches'}
                >
                  <MenuItem value="">{isRtl ? 'كافة الفروع والمطاعم' : 'All Branches'}</MenuItem>
                  {allBranches.map((b) => {
                    const id = b.restaurantBranchId || b.id;
                    return (
                      <MenuItem key={id} value={id}>
                        {b.branchCode} - {b.branchNameAr}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label={isRtl ? 'منطقة التوصيل المستهدفة' : 'Destination Service Zone'}
                  fullWidth
                  size="small"
                  value={commissionRuleForm.destinationServiceZoneId || ''}
                  onChange={(e) => setCommissionRuleForm({ ...commissionRuleForm, destinationServiceZoneId: e.target.value })}
                  helperText={isRtl ? 'اتركه فارغاً للتطبيق على كافة المناطق' : 'Leave empty for all zones'}
                >
                  <MenuItem value="">{isRtl ? 'كافة مناطق الخدمة' : 'All Service Zones'}</MenuItem>
                  {serviceZones.map((z) => {
                    const id = z.serviceZoneId || z.id;
                    return (
                      <MenuItem key={id} value={id}>
                        {z.zoneCode} - {z.zoneNameAr}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>
            </Grid>

            <FormControlLabel
              control={
                <Switch
                  checked={commissionRuleForm.isActive}
                  onChange={(e) => setCommissionRuleForm({ ...commissionRuleForm, isActive: e.target.checked })}
                  color="primary"
                />
              }
              label={t('pricingPage.fields.isActive')}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5, display: 'flex', justifyContent: isRtl ? 'flex-start' : 'flex-end', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleSaveCommissionRule}
            disabled={savingCommissionRule}
            sx={{
              background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
              fontWeight: 700,
            }}
          >
            {savingCommissionRule ? <CircularProgress size={18} color="inherit" /> : t('save')}
          </Button>
          <Button onClick={() => setCommissionRuleModalOpen(false)} color="inherit" size="small">
            {t('cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: isRtl ? 'left' : 'right' }}
      >
        <Alert severity={toast.severity} sx={{ borderRadius: '8px', fontWeight: 600 }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default PricingPlans;

